/**
 * Resolves `principalId → display name` for the active Holder.
 *
 * - **Personal Holder**: there is exactly one Principal (the caller).
 *   The map is just that one entry, rendered as the caller's username,
 *   so e.g. "completed by jane" reads naturally.
 * - **Org Holder**: fetches the full member list via
 *   `GET /vault/holders/:id/members` and indexes by `principalId`.
 *
 * Returns a `resolve(principalId)` helper that falls back gracefully:
 * `displayName` → `@username` → a short redacted id suffix.
 *
 * The query is keyed by holderId so switching Holders refetches; the
 * cache is shared across consumers (e.g. CompletedTimelinePage and
 * any future "assigned to" affordance).
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { useHolderSwitcher } from '@/components/HolderSwitcher';

export interface PrincipalNameResolver {
  resolve: (principalId: string | null | undefined) => string;
  isLoading: boolean;
  /**
   * Active members of the current Org Holder, for assignment pickers. Empty for
   * Personal Holders (a single principal — nothing to assign across).
   */
  members: Array<{ principalId: string; label: string }>;
}

function shortenId(id: string): string {
  // `prn_abc123...` → `prn_abc1…`. Useful when an account was deleted
  // and no display name is available.
  if (id.length <= 12) return id;
  return `${id.slice(0, 8)}…`;
}

export function usePrincipalDisplayNames(): PrincipalNameResolver {
  const { user } = useAuth();
  const { selectedHolderId, selectedOption, principalId: callerPrincipalId } = useHolderSwitcher();

  const isOrg = selectedOption?.holderType === 'org';

  const { data, isLoading } = useQuery({
    queryKey: ['vault', 'holders', selectedHolderId, 'members'],
    queryFn: () => api.listVaultHolderMembers(selectedHolderId as string),
    enabled: !!selectedHolderId && isOrg,
    staleTime: 60_000,
  });

  const map = useMemo(() => {
    const m = new Map<string, string>();
    // Personal Holder: the caller is the only Principal. Use username
    // since AuthUser doesn't carry a separate displayName.
    if (!isOrg && callerPrincipalId && user?.username) {
      m.set(callerPrincipalId, user.username);
    }
    // Org Holder: index the fetched member list.
    if (isOrg && data?.members) {
      for (const member of data.members) {
        const label = member.displayName ?? member.username ?? null;
        if (label) m.set(member.principalId, label);
      }
    }
    return m;
  }, [isOrg, callerPrincipalId, user?.username, data]);

  const members = useMemo<Array<{ principalId: string; label: string }>>(() => {
    if (!isOrg || !data?.members) return [];
    return data.members
      .filter((m) => m.status === 'active')
      .map((m) => ({
        principalId: m.principalId,
        label: m.displayName ?? m.username ?? shortenId(m.principalId),
      }));
  }, [isOrg, data]);

  return useMemo(
    () => ({
      resolve: (principalId: string | null | undefined) => {
        if (!principalId) return '';
        if (callerPrincipalId && principalId === callerPrincipalId) return 'You';
        return map.get(principalId) ?? shortenId(principalId);
      },
      isLoading: isOrg && isLoading,
      members,
    }),
    [map, callerPrincipalId, isOrg, isLoading, members],
  );
}
